import { useQuery } from "@tanstack/react-query";
import { getTradingSystemPayload } from "@app/services/tradingSystemApi";
import type { TradingSystemPayload } from "../../../shared/types";

export function useTradingSystemPayload() {
  return useQuery<TradingSystemPayload>({
    queryKey: ["trading-system", "payload"],
    queryFn: getTradingSystemPayload,
    // SUSTAINABLE SOLUTION: Optimized for fast initial load with cached data
    // Backend returns cached payload immediately (< 1s) if available, otherwise generates fresh data
    // staleTime allows cached data to be used for 10 seconds before considering it stale
    staleTime: 10000, // 10 seconds - aligned with fast refetch interval
    gcTime: 300000, // 5 minutes - keep in cache for 5 minutes for offline fallback
    // Refetch every 10s to keep open-position prices (PnL %) near real-time.
    // The backend overlays live positions (2s cadence) onto the cached payload,
    // so a 10s frontend poll shows fresh prices without regenerating the payload.
    refetchInterval: 10000, // 10 seconds - near real-time position PnL
    refetchOnWindowFocus: true, // Always refetch on focus to ensure fresh data
    // CRITICAL: Only refetch on mount if no cached data exists
    // If cached data exists, use it immediately without blocking refetch
    // Backend returns cached payload instantly if available, then fresh data is generated in background
    // This ensures instant UI display while fresh data loads in background
    refetchOnMount: "always", // Always fetch on mount, but use cached data if available
    // Improved retry logic: 3 retries with exponential backoff
    retry: 3, // 3 retries for network resilience
    retryDelay: (attemptIndex) => {
      // Exponential backoff: 2s, 4s, 8s
      return Math.min(1000 * 2 ** attemptIndex, 8000);
    },
    // Retry on network errors and 5xx server errors, but not on 4xx client errors
    retryOnMount: true,
  });
}

